import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { collectionApi } from '#services/collection';
import {
  ALL_TIME_RANGE_PRESET,
  TimeRangeValue,
  buildTimeRange,
} from '#components/time-range.utils';
import { revertAll } from '../actions';

interface CollectionListState {
  filter: string;
  dateRange: TimeRangeValue;
  filterKey: string;
  order: string;
  orderBy: string;
  pageIndex: number;
  rows: unknown[];
  rowCount: number;
  pageSize: number;
  vendors: Record<string, unknown>;
}

const initialState: CollectionListState = {
  filter: '',
  dateRange: buildTimeRange(ALL_TIME_RANGE_PRESET),
  filterKey: '',
  order: 'asc',
  orderBy: 'name',
  pageIndex: 0,
  rows: [],
  rowCount: 0,
  pageSize: 30,
  vendors: {},
};

const handleGetCollectionsFulfilled = (
  state: CollectionListState,
  action: { payload: unknown },
) => {
  const {
    page,
    rows,
    total,
    vendors,
  } = action.payload as {
    page: number;
    rows: unknown[];
    total: number;
    vendors: Record<string, unknown>;
  };
  state.pageIndex = page;
  state.rows = rows;
  state.rowCount = total;
  state.vendors = vendors;
};

export const collectionListSlice = createSlice({
  name: 'collectionList',
  initialState,
  reducers: {
    setDateRange(state, action: PayloadAction<TimeRangeValue>) {
      state.dateRange = action.payload;
      state.pageIndex = 0;
    },
    setFilter(state, action: PayloadAction<string>) {
      state.filter = action.payload;
    },
    setFilterKey(state, action: PayloadAction<string>) {
      state.filterKey = action.payload;
    },
    setOrder(state, action: PayloadAction<string>) {
      state.order = action.payload;
    },
    setOrderBy(state, action: PayloadAction<string>) {
      state.orderBy = action.payload;
    },
    setPageIndex(state, action: PayloadAction<number>) {
      state.pageIndex = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.pageIndex = 0;
    },
    setRows(state, action: PayloadAction<unknown[]>) {
      state.rows = action.payload;
    },
    setRowCount(state, action: PayloadAction<number>) {
      state.rowCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(revertAll, () => initialState)
      .addMatcher(collectionApi.endpoints.getCollections.matchFulfilled, handleGetCollectionsFulfilled)
      .addMatcher(collectionApi.endpoints.deleteCollection.matchFulfilled, handleGetCollectionsFulfilled);
  },
});

export const {
  setDateRange,
  setFilter,
  setFilterKey,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setRows,
  setRowCount,
} = collectionListSlice.actions;

export default collectionListSlice.reducer;
